/**
 * mapStoryblokVenue
 *
 * Converts the hardcoded Storyblok venue fields (StoryblokHardcodedVenue) into
 * the canonical Venue shape consumed by VenueHero, VenueInfo, VenueClubSchedule
 * and all other venue page section components.
 *
 * This mapper is intentionally pure — it receives only the Storyblok content
 * object and the page's full_slug (used as a stable synthetic `id`) and returns
 * a complete Venue object. No Airtable data is read or modified.
 *
 * Usage
 * ─────
 * import mapStoryblokVenue from "../../utils/mapStoryblokVenue/mapStoryblokVenue.util";
 * const venue = mapStoryblokVenue(content, context.full_slug);
 */

import { Venue, Service, Club } from "../../contexts/VenuesContext";
import {
  StoryblokHardcodedVenue,
  StoryblokHardcodedServiceRow,
  StoryblokHardcodedClubRow,
} from "../../interfaces/Storyblok/StoryblokHardcodedVenue.interface";

/**
 * Parses a comma-separated string of Airtable venue IDs into an array,
 * trimming whitespace and filtering empty entries.
 */
const parseNearbyVenueIds = (raw: string | undefined): string[] => {
  if (!raw) return [];
  return raw
    .split(",")
    .map(id => id.trim())
    .filter(id => id.length > 0);
};

/**
 * Maps a single StoryblokHardcodedServiceRow to the Service shape.
 * `reference` is synthesised from the club type + name to guarantee uniqueness
 * within the page; it is never sent to Airtable so the exact value is not
 * important.
 */
const mapService = (row: StoryblokHardcodedServiceRow): Service => ({
  reference: `hc-${row.hc_service_club_type}-${row.hc_service_name}`.replace(/\s+/g, "-").toLowerCase(),
  name: row.hc_service_name ?? "",
  clubType: row.hc_service_club_type ?? "",
  term: row.hc_service_term ?? "",
  daysRunning: (row.hc_service_days_running ?? "") as unknown as number,
  startDate: row.hc_service_start_date ?? "",
  endDate: row.hc_service_end_date ?? "",
  startTime: row.hc_service_start_time ?? "",
  endTime: row.hc_service_end_time ?? "",
  currentPrice: Number(row.hc_service_current_price) || 0,
  discountedPrice: Number(row.hc_service_discounted_price) || 0,
  tieredPricing: Boolean(row.hc_service_tiered_pricing),
  prices: [],
  bookingStartDate: "",
  bookingEndDate: "",
  earlyFinish: row.hc_service_early_finish ?? "",
  lateFee: Number(row.hc_service_late_fee) || 0,
  cancellationPeriod: row.hc_service_cancellation_period ?? "",
  registrationFee: row.hc_service_registration_fee ?? "",
  bookingCutOff: row.hc_service_booking_cut_off ?? "",
  events: [],
});

/**
 * Maps a single StoryblokHardcodedClubRow to the Club shape (VenuesContext.Club,
 * not FiltersContext.Club — these are per-venue description overrides).
 */
const mapClub = (row: StoryblokHardcodedClubRow): Club => ({
  name: row.hc_club_name ?? "",
  clubType: row.hc_club_type ?? "",
  description: row.hc_club_description ?? "",
  icon: "",
  cardIcon: "",
  websiteFilterItem: "",
  row_idx: 0,
});

/**
 * Maps the full set of StoryblokHardcodedVenue fields to a Venue object.
 *
 * @param content   - The parsed Storyblok story content object
 * @param full_slug - The story's full_slug, used as a synthetic unique `id`
 * @returns         A fully-formed Venue object or null if the content does not
 *                  contain the minimum required hardcoded fields (hc_venue_name)
 */
const mapStoryblokVenue = (
  content: StoryblokHardcodedVenue & Record<string, unknown>,
  full_slug: string,
): Venue | null => {
  if (!content?.hc_venue_name) {
    return null;
  }

  const services: Service[] = Array.isArray(content.hc_services) ? content.hc_services.map(mapService) : [];

  const clubs: Club[] = Array.isArray(content.hc_clubs) ? content.hc_clubs.map(mapClub) : [];

  const venue: Venue = {
    /*
     * Use the full_slug as the synthetic id. This prevents the venue from ever
     * accidentally matching an Airtable record (Airtable IDs are numeric
     * strings) and makes the id stable across rebuilds.
     */
    id: `hc::${full_slug}`,
    name: content.hc_venue_name,
    status: content.hc_status ?? "Live",

    buildingType: {
      name: content.hc_building_type_name ?? "",
      description: "",
      icon: "",
      cardIcon: "",
      websiteFilterItem: "",
      row_idx: 0,
    },

    age: {
      min: Number(content.hc_age_min) || 0,
      max: Number(content.hc_age_max) || 0,
    },

    nearbyVenues: parseNearbyVenueIds(content.hc_nearby_venue_ids as string),

    contactNumber: content.hc_contact_number ?? "",
    showVenuePhoneNumber: Boolean(content.hc_show_phone_number),
    contactEmail: content.hc_contact_email ?? "",

    address: {
      line1: content.hc_address_line1 ?? "",
      line2: content.hc_address_line2 ?? "",
      townCity: content.hc_town_city ?? "",
      county: content.hc_county ?? "",
      postcode: content.hc_postcode ?? "",
      localAuthority: content.hc_local_authority ?? "",
      region: content.hc_region ?? "",
    },

    coords: {
      lat: Number(content.hc_latitude) || 0,
      lng: Number(content.hc_longitude) || 0,
    },

    facilities: [],

    links: {
      website_url: content.hc_website_url ?? "",
      venueWebsite: content.hc_website_url ?? "",
      bookingLink: content.hc_booking_link ?? "",
      availabiltyLink: content.hc_availability_link ?? "",
      registrationLink: content.hc_registration_link ?? "",
    },

    permanentBookingCancellation: "",
    pickupLateFee: 0,
    pickupAndDropoffInstructions: content.hc_pickup_dropoff_instructions ?? "",
    noShowFee: 0,
    dailyDiscount: "",
    weeklyDiscount: "",
    siblingDiscount: "",
    serviceListInfoTitle: content.hc_service_list_info_title ?? "",
    serviceListInfoCopy: content.hc_service_list_info_copy ?? "",

    inspectionStandards: undefined,

    clubs,
    services,

    termsLink: content.hc_terms_link ?? "",
    holidayScheduleLink: content.hc_holiday_schedule_link ?? "",
  };

  return venue;
};

export default mapStoryblokVenue;
